import { z } from 'zod';
import type { BlockDefinition } from '../types';
import { sanitizedHtmlProps } from '../lib/sanitize';

export const tabsSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  h2: z.string().max(200).optional(),
  subtitle: z.string().max(500).optional(),
  variant: z.enum(['underline', 'pills']).default('underline'),
  tabs: z
    .array(
      z.object({
        label: z.string().min(1).max(80),
        title: z.string().max(200).optional(),
        body: z.string().min(1).max(3000),
        image: z.string().max(500).optional(),
        cta_label: z.string().max(60).optional(),
        cta_href: z.string().max(500).optional(),
      }),
    )
    .min(2)
    .max(8),
});

export type TabsData = z.infer<typeof tabsSchema>;

export function TabsBlockRenderer({ eyebrow, h2, subtitle, variant = 'underline', tabs }: TabsData) {
  const safeTabs = Array.isArray(tabs) ? tabs : [];
  return (
    <section className="v5-section v5-tabs-section">
      <div className="container">
        {(eyebrow || h2) && (
          <div className="v5-section-head v5-section-head-left">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            {h2 ? <h2 className="v5-h2">{h2}</h2> : null}
            {subtitle ? <p className="v5-lead">{subtitle}</p> : null}
          </div>
        )}
        <div className={`v5-tabs v5-tabs-${variant}`}>
          <div className="v5-tabs-list" role="tablist">
            {safeTabs.map((t, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-controls={`tab-panel-${i}`}
                id={`tab-${i}`}
                data-tab-index={i}
                aria-selected={i === 0 ? 'true' : 'false'}
                className={`v5-tab${i === 0 ? ' is-active' : ''}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {safeTabs.map((t, i) => (
            <div
              key={i}
              id={`tab-panel-${i}`}
              role="tabpanel"
              aria-labelledby={`tab-${i}`}
              hidden={i !== 0}
              className="v5-tab-panel"
            >
              {t.image ? <img className="v5-tab-image" src={t.image} alt={t.title || t.label} loading="lazy" /> : null}
              {t.title ? <h3 className="v5-tab-title">{t.title}</h3> : null}
              <div className="v5-tab-body" dangerouslySetInnerHTML={sanitizedHtmlProps(t.body)} />
              {t.cta_label && t.cta_href ? (
                <a className="v5-btn-outline" href={t.cta_href}>{t.cta_label}</a>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const tabsBlock: BlockDefinition<TabsData> = {
  meta: {
    type: 'tabs',
    label: 'Tabs / Pestañas',
    description: 'Conjunto de pestañas con contenido HTML enriquecido, imagen opcional y CTA.',
    category: 'content',
    icon: 'columns',
  },
  schema: tabsSchema,
  defaults: {
    eyebrow: 'CARACTERÍSTICAS',
    h2: 'Una plataforma, varias herramientas',
    variant: 'underline',
    tabs: [
      {
        label: 'CRM',
        title: 'Gestiona leads y clientes',
        body: '<p>Captura, clasifica y haz seguimiento de cada oportunidad en un único lugar.</p>',
      },
      {
        label: 'Analytics',
        title: 'Mide lo importante',
        body: '<p>Dashboards en tiempo real con los KPIs que mueven tu negocio.</p>',
      },
      {
        label: 'Automatización',
        title: 'Menos tareas, más impacto',
        body: '<p>Workflows visuales sin código.</p>',
      },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'variant',
      label: 'Estilo',
      type: 'select',
      options: [
        { label: 'Underline (clásico)', value: 'underline' },
        { label: 'Pills (pastillas)', value: 'pills' },
      ],
    },
    {
      name: 'tabs',
      label: 'Pestañas',
      type: 'array',
      max: 8,
      itemFields: [
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 80 },
        { name: 'title', label: 'Título dentro', type: 'text', max: 200 },
        { name: 'body', label: 'Contenido', type: 'richtext', required: true },
        { name: 'image', label: 'Imagen (opcional)', type: 'image' },
        { name: 'cta_label', label: 'CTA texto', type: 'text', max: 60 },
        { name: 'cta_href', label: 'CTA URL', type: 'url' },
      ],
    },
  ],
  Renderer: TabsBlockRenderer,
};
